import React, { FC } from 'react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { faKeyboard } from '@fortawesome/free-solid-svg-icons/faKeyboard';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFieldArrayAppend, useFormContext } from 'react-hook-form';
import classNames from 'classnames';

import { AttachmentGroups } from '@buyside/components/presentation/IdeaForm/WriteupAndAttachments/writeupAndAttachments';
import AttachmentForm from '@buyside/components/presentation/IdeaForm/WriteupAndAttachments/Attachments/AttachmentForm/attachmentForm';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import Icon from '@buyside/components/presentation/Icon/icon';

import { IdeaFormInput } from '@buyside/util/researchSubmission';

import fieldStyles from '@buyside/components/presentation/Fields/fieldStyles.scss';
import styles from './writeupStyles.scss';

const ThesisTypeSelection: FC<{
  append: UseFieldArrayAppend<IdeaFormInput, 'attachments'>;
  handleRemoveFormattedThesisAttachment: () => void;
  handleRemoveThesisAttachment: () => void;
  setThesisEditorHidden: (hidden: boolean) => void;
  thesisAttachmentGroup: AttachmentGroups['Thesis'];
}> = ({
  append,
  handleRemoveFormattedThesisAttachment,
  handleRemoveThesisAttachment,
  setThesisEditorHidden,
  thesisAttachmentGroup
}) => {
  const {
    formState: { errors },
    setValue,
    trigger
  } = useFormContext<IdeaFormInput>();

  const thesisAttachmentPreview = Object.values(thesisAttachmentGroup.attachmentPreviewIndices)[0];

  return (
    <div className={classNames(fieldStyles.field, fieldStyles.medium)}>
      <div className={styles.thesisSelection}>
        <div
          className={classNames(styles.thesisSelectionGroup, {
            [styles.hasError]: Boolean(errors.thesis)
          })}
        >
          <div>
            <p>
              <FontAwesomeIcon className={styles.thesisSelectionGroupIcon} icon={faKeyboard} />
              <strong>Type Your Thesis</strong>
            </p>
            <p>Enter your thesis directly into the form using our editor.</p>
          </div>
          <div>
            <Button
              buttonType={BUTTON_TYPES.BUTTON}
              faIcon={faPlus}
              onClick={() => {
                setValue('thesis', '', {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true
                });
                handleRemoveThesisAttachment();
                setThesisEditorHidden(false);
              }}
              size={BUTTON_SIZES.SMALL}
              text={`Type Thesis${thesisAttachmentPreview ? ' Instead' : ''}`}
              type={BUTTON_TYPES.LINK}
            />
          </div>
        </div>
        <div
          className={classNames(styles.thesisSelectionGroup, {
            [styles.hasError]: Boolean(errors.thesis)
          })}
        >
          <div>
            <p>
              <Icon className={styles.thesisSelectionGroupIcon} type={thesisAttachmentGroup.icon} />
              <strong>Attach Your Thesis</strong>
            </p>
            <p>{thesisAttachmentGroup.description}</p>
          </div>
          <div>
            {thesisAttachmentPreview ? (
              thesisAttachmentPreview
            ) : (
              <AttachmentForm
                onSubmitCallback={values => {
                  handleRemoveFormattedThesisAttachment();
                  append(values);
                  trigger('thesis');
                }}
                researchType="Thesis"
              />
            )}
          </div>
        </div>
      </div>
      {errors.thesis ? (
        <label data-error className={styles.thesisError}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {errors.thesis.message}
        </label>
      ) : null}
    </div>
  );
};

export default ThesisTypeSelection;
